import type {
	Currency,
	CustomerOrgType,
	IPaginatedResponse
} from '../../types';

export enum BillingPaymentStatus {
	Failed = 'failed',
	Pending = 'pending',
	Refunded = 'refunded',
	Succeeded = 'succeeded'
}

export enum BillingSubscriptionStage {
	Development = 'development',
	Trial = 'trial',
	Paid = 'paid'
}

export enum BillingCreateCustomerSubscriptionStatus {
	Active = 'active',
	PaymentConfirmation = 'payment_confirmation',
	RequiresAction = 'requires_action',
	Trialing = 'trialing'
}

export enum BillingUsagePaymentStatus {
	Failed = 'failed',
	Paid = 'paid',
	Pending = 'pending',
	Uncollectible = 'uncollectible',
	Voided = 'voided'
}

export enum BillingCustomerDebtInvoiceStatus {
	Failed = 'failed',
	Pending = 'pending',
	Uncollectible = 'uncollectible'
}

export enum StripeConnectionStatus {
	NotConnected = 'not_connected',
	Pending = 'pending',
	Connected = 'connected',
	Restricted = 'restricted',
	Disconnected = 'disconnected'
}

export const STRIPE_CONNECT_STATUS_QUERY_PARAM = 'stripe_connection_status';

export enum BillingProductKey {
	Enterprise = 'monthly_enterprise',
	AnnualEnterprise = 'annual_enterprise',
	PayAsYouGo = 'monthly_payg',
	Regular = 'monthly_regular',
	Starter = 'monthly_starter',
	Small = 'monthly_small',
	AnnualSmall = 'annual_small',
	Medium = 'monthly_medium',
	AnnualMedium = 'annual_medium',
	Large = 'monthly_large',
	AnnualLarge = 'annual_large',
	Corp = 'monthly_corp',
	AnnualCorp = 'annual_corp',
	PartnerTier0 = 'partner_tier_0',
	PartnerTier1 = 'partner_tier_1',
	PartnerTier2 = 'partner_tier_2',
	PartnerTier3 = 'partner_tier_3'
}

export enum ProductPeriodType {
	Recurring = 'recurring',
	SingleUse = 'single_use'
}

export enum BillingCommitmentType {
	Annual = 'annual',
	Monthly = 'monthly',
	Other = 'other'
}

export enum TransactionType {
	PARTNER_MLF_COMMITMENT = 'partner_mlf_commitment',
	PARTNER_MLF_PAYMENT = 'partner_mlf_payment',
	PARTNER_CASH_OUT = 'partner_cash_out',
	PARTNER_RETURN_FROM_DEPOSIT = 'partner_return_from_deposit',
	ORGANIZATION_PARTNER_COMMISSION = 'organization_partner_commission'
}

export type DiscountInfo = {
	coupon_name: string;
	percent_off: number | null;
	amount_off: number | null;
	duration_in_months: number | null;
	start?: string | null;
	end?: string | null;
};

export type TBillingProduct = {
	id: string;
	key: BillingProductKey;
	name: string;
	description: string;
	hint: string;
	price: number | null;
	currency: Currency | null;
	units_total: number;
	units_voice: number;
	units_text: number;
	commitment_type: BillingCommitmentType;
	organization_type: CustomerOrgType;
	trial_days: number;
	period_type?: ProductPeriodType;
	checkout_available?: boolean;
	is_custom: boolean;
};

export type TBillingSubscriptionInfo = {
	subscription_start: string;
	subscription_end?: string;
	subscription_stage: BillingSubscriptionStage;
	subscription_update_allowed: boolean;
	is_partner_subscription?: boolean;
	trial_start?: string | null;
	trial_end?: string | null;
	product_key?: BillingProductKey | null;
	product_name?: string | null;
	product_quantity: number;
	product_units: number;
	unit_price_amount?: number;
	unit_price_currency?: string;
	product_info?: TBillingProduct;
	discount?: DiscountInfo;
};

export type TBillingSecondaryProduct = {
	name: string;
	quantity: number;
};

export type TBillingPaymentMethod = {
	type: string;
	data: Record<string, unknown>;
	created_at: string;
};

export type TBillingSavedPaymentMethod = {
	id: string;
	brand: string | null;
	last4: string | null;
	exp_month: number | null;
	exp_year: number | null;
	billing_name: string | null;
	is_expired: boolean;
	is_default: boolean;
};

export type TBillingPaymentMethodsResponse = {
	items: TBillingSavedPaymentMethod[];
	default_payment_method_id: string | null;
};

export type TBillingCustomerDebt = {
	currency: Currency;
	invoices_amount: number;
	invoices?: TBillingCustomerDebtInvoice[];
	payment_required: boolean;
	total: number;
	units?: number | null;
	units_amount: number;
};

export type TBillingCustomerDebtInvoice = {
	amount_minor: number;
	awaiting_action: boolean;
	currency: Currency;
	id: string;
	status: BillingCustomerDebtInvoiceStatus;
	units: number | null;
};

export type TBillingPayUsageDebtData = {
	invoice_id: string;
	payment_method_id?: string;
};

export type TBillingPayOverageData = {
	payment_method_id?: string;
};

export type TBillingUsagePaymentResponse = {
	amount_minor: number;
	client_secret?: string | null;
	currency: Currency;
	invoice_id: string;
	status: BillingUsagePaymentStatus;
};

export type TCreateBillingPaymentMethodSetupResponse = {
	client_secret: string | null;
};

export type TBillingCustomerGrace = {
	day: number;
	daily_spent: number;
	daily_limit: number;
	block_day: number;
	final_day: number;
};

export type TBilling = {
	debt?: TBillingCustomerDebt | null;
	payment_method: TBillingPaymentMethod | null;
	units_spent: number | null;
	grace?: TBillingCustomerGrace | null;
	unit_balance?: number | null;
	units_warning_threshold?: number | null;
	checkout_completed: boolean;
	subscription_info: TBillingSubscriptionInfo | null;
	secondary_products: TBillingSecondaryProduct[];
	/** Payment account supplied by GET billing; null or absent uses the platform. */
	stripe_account_id?: string | null;
};

export type TStripeConnection = {
	stripe_account_id: string | null;
	status: StripeConnectionStatus;
};

export type TStripeConnectAuthorizationData = {
	redirect_url: string;
};

export type TBillingStripeFlowType =
	| 'subscription_update'
	| 'subscription_cancel';

export type TBillingStripeUrlData = {
	return_url: string;
	flow_data_type: TBillingStripeFlowType;
};

export type TBillingUnitsUsageParams = {
	start_at: string;
	end_at: string;
};

export type TBillingUnitsUsageItem = {
	start_at: string;
	end_at: string;
	voice_units: number;
	text_units: number;
};

export type TBillingUnitsUsageResponse = {
	period: {
		start_at: string;
		end_at: string;
		source: string;
	};
	items: TBillingUnitsUsageItem[];
	aggregates: {
		voice_units: number;
		text_units: number;
		total_units: number;
	};
};

export type TBillingCheckoutUrlData = {
	customer_idn: string;
	success_url: string;
	product_key: BillingProductKey;
	trial_enabled: boolean;
	development_stage_enabled?: boolean;
	promotion_code?: string;
};

export type TBillingCheckoutUrlResponse = {
	url: string;
};

export type TBillingCreateCustomerSubscriptionData = {
	customer_idn: string;
	payment_method_id: string;
	product_key: BillingProductKey;
};

export type TBillingCreateCustomerSubscriptionResponse = {
	client_secret?: string | null;
	currency: Currency;
	current_period_end: number;
	current_period_start: number;
	subscription_id?: string | null;
	status: BillingCreateCustomerSubscriptionStatus;
};

export type TBillingTransactionsParams = {
	page: number;
	per: number;
};

export type TBillingPaymentsParams = {
	limit?: number;
};

export type TBillingProductsParams = {
	commitment_type?: Exclude<BillingCommitmentType, BillingCommitmentType.Other>;
	currency?: Currency;
	organization_type?: CustomerOrgType;
	tenant?: string;
};

export type TProductPrice = {
	amount: number;
	currency: Currency;
};

export type TProduct = {
	id: string;
	key: string;
	name: string;
	description: string;
	hint: string;
	tenant: string;
	units_total: number;
	units_voice: number;
	units_text: number;
	hard_quota: number | null;
	soft_quota: number | null;
	is_custom: boolean;
	commitment_type: BillingCommitmentType;
	organization_type: CustomerOrgType;
	trial_days: number;
	display_enabled: boolean;
	display_order: number;
	period_type: ProductPeriodType;
	checkout_available: boolean;
	external_product_id: string | null;
	prices: TProductPrice[];
};

export type TCreateProductData = {
	name: string;
	prices: TProductPrice[];
	soft_quota: number | null;
	hard_quota: number | null;
	display_enabled: boolean;
	display_order: number;
	period_type: ProductPeriodType;
	commitment_type: Exclude<BillingCommitmentType, BillingCommitmentType.Annual>;
	trial_days: number;
};

export type TUpdateProductData = {
	name: string;
	prices?: TProductPrice[];
	soft_quota: number | null;
	hard_quota: number | null;
	display_enabled: boolean;
	display_order: number;
};

export type TBillingPayment = {
	amount: number;
	created_at: string;
	currency: Currency;
	description?: string | null;
	paid: boolean;
	status: BillingPaymentStatus;
};

export type TBillingTransactionSourceType = 'current' | 'deposit' | 'external';

export type TPartnerServiceLevel = 'a' | 'b' | 'c';

export type TBillingTransaction = {
	id: string;
	source_organization_id: string | null;
	debit_account_name: string | null;
	credit_account_name: string | null;
	reason_code: TransactionType;
	source_organization_idn: string | null;
	source_organization_name: string | null;
	target_organization_id: string | null;
	target_organization_idn: string | null;
	target_organization_name: string | null;
	source_type: TBillingTransactionSourceType;
	target_type: TBillingTransactionSourceType;
	amount: number;
	created_at: string;
	partner_service_level: TPartnerServiceLevel | null;
	partner_commission: string | null;
	partner_member_id: string | null;
	partner_member_idn: string | null;
	partner_member_name: string | null;
	partner_member_email: string | null;
	partner_member_tenant: string | null;
	organization_id: string | null;
	organization_idn: string | null;
	organization_name: string | null;
	organization_payment_amount: number | null;
};

export type TBillingTransactionsResponse =
	IPaginatedResponse<TBillingTransaction>;

export type TStripeConnectAuthorizationResponse = {
	authorization_url: string;
};
